import { z } from 'zod';

export const paginationShape = {
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
};

export const pageInfoSchema = z.object({
  count: z.int().min(0),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
  limit: z.int().min(0),
  nextOffset: z.int().min(0).optional(),
  offset: z.int().min(0),
  previousOffset: z.int().min(0).optional(),
  totalItems: z.int().min(0),
});

export type PageInfo = z.infer<typeof pageInfoSchema>;

export type PaginatedItems<T> = {
  items: T[];
  pageInfo: PageInfo;
};

export type Pagination = Required<PaginationOptions>;

export type PaginationOptions = NonNullable<z.infer<typeof paginationSchema>>;

const refinePagination = <Schema extends z.ZodObject>(schema: Schema) =>
  schema.refine(
    ({ limit, offset }) =>
      (limit === undefined && offset === undefined)
      || (limit !== undefined && offset !== undefined),
    {
      message:
        'limit and offset must either both be provided or both be omitted',
    },
  );

export const paginationSchema = refinePagination(
  z.object(paginationShape),
).optional();

export const withPaginationSchema = <Shape extends z.ZodRawShape>(
  shape: Shape,
) => refinePagination(z.object({ ...paginationShape, ...shape }));

export const parsePagination = (
  pagination?: PaginationOptions,
): Pagination | undefined => {
  const result = paginationSchema.safeParse(pagination);

  if (!result.success) {
    throw result.error;
  }

  const { limit, offset } = result.data ?? {};

  if (limit === undefined || offset === undefined) {
    return undefined;
  }

  return {
    limit,
    offset,
  };
};

export const paginateItems = <T>(
  items: T[],
  paginationOptions: PaginationOptions,
): PaginatedItems<T> => {
  const totalItems = items.length;
  const pagination = parsePagination(paginationOptions);

  if (!pagination) {
    return {
      items,
      pageInfo: {
        count: totalItems,
        hasNextPage: false,
        hasPreviousPage: false,
        limit: totalItems,
        offset: 0,
        totalItems,
      },
    };
  }

  const endOffset = pagination.offset + pagination.limit;
  const hasNextPage = endOffset < totalItems;
  const hasPreviousPage = pagination.offset > 0 && totalItems > 0;
  const paginatedItems = items.slice(pagination.offset, endOffset);

  return {
    items: paginatedItems,
    pageInfo: {
      count: paginatedItems.length,
      hasNextPage,
      hasPreviousPage,
      limit: pagination.limit,
      offset: pagination.offset,
      totalItems,
      ...(hasNextPage ? { nextOffset: endOffset } : {}),
      ...(hasPreviousPage
        ? {
            previousOffset: Math.max(pagination.offset - pagination.limit, 0),
          }
        : {}),
    },
  };
};
